import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { IUserDetails, IApplyFilterOnLanding, IFilterModel } from './app.model';
import { IEvent, IEventPublisher } from '@irb-common/events';
import { UtilityService } from './irb-common/services/utility.service';

@Injectable({
  providedIn: 'root'
})
export class AppService implements IEventPublisher {
    private userDetails: IUserDetails;
    private sessionStore: any = {};
    private localStore: any = {};
    private store: any = {};
    protected MAX_FILE_UPLOAD_SIZE: number;

    event = new Subject<IEvent>();
    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type':  'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT',
            'Access-Control-Allow-Origin': location.hostname
        })
    };

    constructor(private _http: HttpClient, private route: ActivatedRoute, private util: UtilityService) {
        this.getUserDetails().subscribe((d: IUserDetails) => {
            if(typeof d !== "undefined") {
                // vulnerability: reading json from storage. Json contains html tags. Those later will be presented
                // on a screen.
                let s = window.sessionStorage.getItem("IRB_SESSION_STORE_" + d.userID);

                try {
                    this.sessionStore = (s != null) ? JSON.parse(s) : {};
                } catch(e) {}

                s = window.localStorage.getItem("IRB_APPLICATION_STORE_" + d.userID);

                try {
                    this.localStore = (s != null) ? JSON.parse(s) : {};
                } catch(e) {}
            }
        });
    }

    getUserDetails(): Observable<Object> {
        if(this.userDetails) {
            return of(this.userDetails);
        } else {
            return new Observable(obs => {
                var timer = setInterval(() => {
                    if (this.getItem('IRB_APP_CONFIG')) {
                        this.userDetails = this.getItem('IRB_APP_CONFIG') as IUserDetails;
                        obs.next(this.userDetails);
                        clearInterval(timer);
                        obs.complete();
                    } else {
                        obs.next(undefined);
                    }
                }, 0);
            });
        }
    }

    setItem(key: string, value: any) {
        this.store[key] = value;
    }

    getItem(key: string): any {
        return this.store[key];
    }

    removeItem(key: string): any {
        delete this.store[key];
    }

    clear() {
        this.store = {};
    }

    setToSession(key: string, value: any) {
        // console.warn("Do not use session storage for private information.");
        this.sessionStore[key] = value;
        this.saveSessionStore();
    }

    getFromSession(key: string, _default?: any): any {
        return (typeof this.sessionStore[key] !== "undefined") ? this.sessionStore[key] : _default;
    }

    extractFromSession(key: string): any {
        let v = this.sessionStore[key];
        delete this.sessionStore[key];
        this.saveSessionStore();
        return v;
    }

    saveSessionStore() {
        this.getUserDetails().subscribe((d: IUserDetails) => {
            if(typeof d !== "undefined") {
                window.sessionStorage.setItem("IRB_SESSION_STORE_" + d.userID, JSON.stringify(this.sessionStore));
            }
        });
    }

    setToLocal(key: string, value: any) {
        // console.warn("Do not use local storage for private information.");
        this.localStore[key] = value;
        this.saveLocalStore();
    }

    getFromLocal(key: string): any {    
        return this.localStore[key];
    }

    extractFromLocal(key: string): any {
        let v = this.localStore[key];
        delete this.localStore[key];
        this.saveLocalStore();
        return v;
    }

    saveLocalStore() {
        this.getUserDetails().subscribe((d: IUserDetails) => {
            if(typeof d !== "undefined") {
                window.localStorage.setItem("IRB_APPLICATION_STORE_" + d.userID, JSON.stringify(this.localStore));
            }
        });
    }

    getEvents(): Observable<IEvent> {
        return this.event.asObservable();
    }

    fireEvent(type: string, data?: any) {
        this.event.next({
            "type": type,
            "data": data
        } as IEvent);
    }

    getWindowsState(){
        return window.history.state as IApplyFilterOnLanding
    }

    getFieldInfo(state: IApplyFilterOnLanding, fieldName:string){
        if(state && fieldName){
            const filterModel:IFilterModel = state.filterModel;
            if(filterModel && filterModel.name === fieldName){
                return filterModel;
            }
        }
    }
    
    logOut() {
        this._http.delete("/irebalAuthService/", this.httpOptions).subscribe(() => {
            this._http.get<any>('/logout', this.httpOptions).subscribe(() => {
                this.close();
            }, () => {
                this.close();
            });
        });
    }

    close() {
        const s = this.getItem("IRB_APP_CONFIG");

        if(s !== null) {
            const veoRedirectUrl = s.veoRedirectUrl;
            this.store = {};
            window.sessionStorage.clear();
            this.util.deleteAllCookies();

            if (veoRedirectUrl) {
                setTimeout(() => window.location.href = veoRedirectUrl, 0);
            }
        }
        
        window.close();
    }

    maxFileUploadSizeExceeded(file: any): boolean {
        const userDetails = this.getItem('IRB_APP_CONFIG') as IUserDetails;
        this.MAX_FILE_UPLOAD_SIZE = userDetails?.maxImportFileSize || 52428800;
        return file.size > this.MAX_FILE_UPLOAD_SIZE;
    }

    runThatCall(url: string) {
        return this._http.get(url, this.httpOptions)
    }
}
